import { useTranslation } from 'react-i18next';

import { localizedTourFacts } from '@todo/core/i18n/tour-content';
import type { Locale } from '@todo/core/i18n/locales';
import type { Tour } from '@todo/core/tours';

import css from './tour-facts.module.css';

// The tour page's fact blocks under the description: included / not included,
// meeting & pickup, know before you go — straight from Viator's product data
// (tours.details, written by sync-viator). The supplier's lines are English;
// translate-tours translates them for the DE / RU pages
// (translations.<locale>.facts), and a list not translated yet shows the
// English lines with lang="en". The headings, the pickup option and the
// language names are localized here.
export function TourFacts({ tour, locale }: { tour: Tour; locale: Locale }) {
  const { t } = useTranslation();
  const details = tour.details;
  if (!details) return null;

  const included = localizedTourFacts(tour, 'included', locale);
  const excluded = localizedTourFacts(tour, 'excluded', locale);
  const knowBefore = localizedTourFacts(tour, 'knowBefore', locale);
  const pickup = details.pickup ? t(`tourFacts.pickupOptions.${details.pickup}`, { defaultValue: '' }) : '';
  const group =
    details.privateTour === true
      ? t('tourFacts.private')
      : details.privateTour === false && details.maxTravelers
        ? t('tourFacts.shared', { count: details.maxTravelers })
        : '';
  const languages = languageNames(details.guideLanguages ?? [], locale);
  const meeting = [pickup, group, languages && t('tourFacts.languages', { languages })].filter(Boolean);

  if (!included.items.length && !excluded.items.length && !meeting.length && !knowBefore.items.length) return null;

  return (
    <div className={css.wrap}>
      {included.items.length || excluded.items.length ? (
        <section className={css.section}>
          <h2 className={css.heading}>{t('tourFacts.includedTitle')}</h2>
          <div className={css.columns}>
            {included.items.length ? (
              <ul className={css.list} aria-label={t('tourFacts.included')} lang={included.lang}>
                {included.items.map((line) => (
                  <li key={line} className={css.yes}>
                    {line}
                  </li>
                ))}
              </ul>
            ) : null}
            {excluded.items.length ? (
              <ul className={css.list} aria-label={t('tourFacts.excluded')} lang={excluded.lang}>
                {excluded.items.map((line) => (
                  <li key={line} className={css.no}>
                    {line}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}

      {meeting.length ? (
        <section className={css.section}>
          <h2 className={css.heading}>{t('tourFacts.meetingTitle')}</h2>
          <ul className={css.list}>
            {meeting.map((line) => (
              <li key={line} className={css.dot}>
                {line}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {knowBefore.items.length ? (
        <section className={css.section}>
          <h2 className={css.heading}>{t('tourFacts.knowBeforeTitle')}</h2>
          <ul className={css.list} lang={knowBefore.lang}>
            {knowBefore.items.map((line) => (
              <li key={line} className={css.dot}>
                {line}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ISO codes → names in the page's language ("ar" → "Arabisch"); an unknown
// code stays as it is. Same output at export time and in the browser (ICU).
function languageNames(codes: string[], locale: string): string {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'language' });
  } catch {
    names = null;
  }
  return codes.map((code) => names?.of(code) ?? code).join(', ');
}
